/**
 * Session-artifact focus classification — ONE predicate for "does this
 * `session` artifact name a specific surface" that the Work canvas host, the
 * refresh persistence and the harness auto-open policy all read.
 *
 * A FOCUSED session artifact pins a concrete surface: Context, Transcript,
 * Note, or a Document identified by id. Work renders these as the focused V2
 * pane and the agent may auto-open them.
 *
 * A GENERIC session artifact (no focus, or a Document focus with no id) is a
 * pointer at the whole session. With the focused-pane flag on, Work never
 * renders it on the RHS: the agent does not auto-open it, and a click on it
 * takes the clinician to the full Session page instead.
 */

import type { TabType } from '@heidi/session/contracts';

import type { Artifact } from './state';

type SessionArtifact = Extract<Artifact, { type: 'session' }>;

// `TabType` crosses agents -> session as a TYPE only (`DOMAIN_EDGES.agents` is
// empty, so the enum VALUE may not be imported here). The members are asserted
// from their wire strings, which the persisted record already compares against.
const CONTEXT_TAB = 'context' as TabType;
const TRANSCRIPT_TAB = 'transcript' as TabType;
const NOTE_TAB = 'note' as TabType;
export const DOCUMENT_TAB = 'document' as TabType;

const FOCUS_ONLY_TABS: ReadonlySet<TabType> = new Set([
  CONTEXT_TAB,
  TRANSCRIPT_TAB,
  NOTE_TAB,
]);

const isSessionArtifact = (
  artifact: Artifact | null | undefined
): artifact is SessionArtifact => artifact?.type === 'session';

/** A `session` artifact pinned to Context, Transcript, Note, or an identified Document. */
export const isFocusedSessionArtifact = (
  artifact: Artifact | null | undefined
): artifact is SessionArtifact => {
  if (!isSessionArtifact(artifact)) return false;
  const { focusTab, focusDocumentId } = artifact;
  if (!focusTab) return false;
  if (FOCUS_ONLY_TABS.has(focusTab)) return true;
  return focusTab === DOCUMENT_TAB && Boolean(focusDocumentId);
};

/** A `session` artifact that points at the whole session rather than one surface. */
export const isGenericSessionArtifact = (
  artifact: Artifact | null | undefined
): artifact is SessionArtifact =>
  isSessionArtifact(artifact) && !isFocusedSessionArtifact(artifact);
